import React, { useState, useEffect, useRef } from 'react';
import CoreHttpHandler from 'http/services/CoreHttpHandler';
import moment from 'moment';
import TemplateAnalyticsHeader from './TemplateAnalyticsHeader';
import TemplateReportsTable from './TemplateReportsTable';
import StatsGrid from './StatsGrid';
import { useTheme } from '@material-ui/core/styles';
import { Grid } from '@material-ui/core';
import ConversionFunnel from './ConversionFunnel';
import ResponseBreakdownChart from './ResponseBreakdownChart';
import StatusDistributionChart from './StatusDistributionChart';
import ResponseTimeDistributionChart from './ResponseTimeDistributionChart';
import WeeklyTrendChart from './WeeklyTrendChart';
import FlowAnalyticsChart from './FlowAnalyticsChart';

function TemplateAnalytics({ data = null }) {
    const theme = useTheme();

    // Loading states
    const [loadingFunnelAnalytics, setLoadingFunnelAnalytics] = useState(false);
    const [loadingReportsAnalytics, setLoadingReportsAnalytics] = useState(false);
    const [loadingReports, setLoadingReports] = useState(false);
    const [loadingTemplates, setLoadingTemplates] = useState(false);

    // Data states
    const [webhookFunnelAnalyticsData, setWebhookFunnelAnalyticsData] = useState(null);
    const [webhookReportsAnalyticsData, setWebhookReportsAnalyticsData] = useState(null);
    const [webhookReportsData, setWebhookReportsData] = useState([]);
    const [templatesData, setTemplatesData] = useState([]);
    const [totalItems, setTotalItems] = useState(0);

    // Global Date Range State (controlled by header)
    // Default for this page: start date = yesterday, end date = today
    const [dateRange, setDateRange] = useState({
        startDate: moment.utc().subtract(1, 'day').startOf('day'),
        endDate: moment.utc().endOf('day')
    });

    // Compare Date Range State (for ConversionFunnel)
    // Compare Date Range State (for ConversionFunnel) - Default to previous week
    const [compareDateRange, setCompareDateRange] = useState({
        compareStartDate: moment.utc().startOf('day'),
        compareEndDate: moment.utc().endOf('day')
    });

    // Group By filter (for StatsGrid and Charts)
    const [groupBy, setGroupBy] = useState('week');

    // Table filters
    const [tableFilters, setTableFilters] = useState({
        sortBy: 'ASC',
        orderBy: 'dt',
        user_reply: '',
        customer_number: ''
    });

    //Selected Template Id
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);

    // Pagination state
    const [currentParams, setCurrentParams] = useState({ limit: 10, page: 0 });

    // Track previous filter values to detect changes
    const prevCompareDateRef = useRef(compareDateRange);
    const prevGroupByRef = useRef(groupBy);
    const prevTableFiltersRef = useRef(tableFilters);

    // Fetch function for webhookFunnelAnalytics
    const fetchWebhookFunnelAnalyticsData = () => {
        setLoadingFunnelAnalytics(true);

        const payload = {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
        };

        if (!payload.startDate || !payload.endDate) {
            return;
        }

        if (data?.id) {
            payload.clientId = data?.id;
        }

        // Add templateId filter
        if (selectedTemplateId) {
            payload.templateId = selectedTemplateId;
        }
        // Add compare dates only if both are set (not null)
        if (compareDateRange.compareStartDate && compareDateRange.compareEndDate) {
            payload.compareStartDate = compareDateRange.compareStartDate;
            payload.compareEndDate = compareDateRange.compareEndDate;
        }

        const onSuccess = (response) => {
            setWebhookFunnelAnalyticsData(response?.data?.data?.data);
            setLoadingFunnelAnalytics(false);
        };

        const onError = (error) => {
            console.error('Error fetching Webhook Funnel Analytics:', error);
            setLoadingFunnelAnalytics(false);
        };

        CoreHttpHandler.request(
            'analytics',
            'webhookFunnelAnalytics',
            payload,
            onSuccess,
            onError
        );
    };

    // Fetch function for webhookReportsAnalytics
    const fetchWebhookReportsAnalyticsData = () => {
        setLoadingReportsAnalytics(true);

        const payload = {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            groupBy: groupBy
        };

        if (!payload.startDate || !payload.endDate) {
            return;
        }
        if (data?.id) {
            payload.clientId = data?.id;
        }
        // Add templateId filter
        if (selectedTemplateId) {
            payload.templateId = selectedTemplateId;
        }

        const onSuccess = (response) => {
            setWebhookReportsAnalyticsData(response?.data?.data);
            setLoadingReportsAnalytics(false);
        };

        const onError = (error) => {
            console.error('Error fetching Webhook Reports Analytics:', error);
            setLoadingReportsAnalytics(false);
        };

        CoreHttpHandler.request(
            'analytics',
            'webhookReportsAnalytics',
            payload,
            onSuccess,
            onError
        );
    };

    // Fetch function for webhookReports
    const fetchWebhookReportsData = () => {
        setLoadingReports(true);


        const payload = {
            sortby: tableFilters.sortBy,
            orderby: tableFilters.orderBy,
            limit: currentParams.limit,
            page: currentParams.page
        };
        if (data?.id) {
            payload.clientId = data?.id;
        }

        // Add startDate and endDate only if both are not null
        if (dateRange.startDate && dateRange.endDate) {
            payload.startDate = dateRange.startDate;
            payload.endDate = dateRange.endDate;
        }

        if (!payload.startDate || !payload.endDate) {
            return;
        }

        // Add templateId filter
        if (selectedTemplateId) {
            payload.templateId = selectedTemplateId;
        }

        // Add optional filters only if they have values
        if (tableFilters.user_reply) {
            payload.user_reply = tableFilters.user_reply;
        }
        if (tableFilters.customer_number) {
            payload.customer_number = tableFilters.customer_number;
        }

        const onSuccess = (response) => {
            const data = response.data?.data?.data || [];
            const total = response?.data.data?.totalItems || 0;

            setWebhookReportsData(data);
            setTotalItems(total);
            setLoadingReports(false);
        };

        const onError = (error) => {
            console.error('Error fetching Webhook Reports:', error);
            setWebhookReportsData([]);
            setTotalItems(0);
            setLoadingReports(false);
        };

        CoreHttpHandler.request(
            'analytics',
            'webhookReports',
            payload,
            onSuccess,
            onError
        );
    };
    const fetchTemplatesData = () => {
        setLoadingTemplates(true);
        const payload = {};
        if (data?.id) {
            payload.clientId = data?.id;
        }
        const onSuccess = (response) => {
            const data = response.data?.data?.data || [];
            setTemplatesData(data);
            setLoadingTemplates(false);
        };

        const onError = (error) => {
            console.error('Error Fetching Templates:', error);
            setTemplatesData([]);
            setLoadingTemplates(false);
        };

        CoreHttpHandler.request(
            'templateMessagesMFMS',
            'fetch_template_by_client',
            payload,
            onSuccess,
            onError
        );
    };

    // Handler for template filter change
    const handleTemplateFilterChange = (templateId) => {
        setSelectedTemplateId(templateId);
    };
    // Fetch all APIs
    const fetchAllData = () => {
        // Only fetch if both dates are set
        if (dateRange.startDate && dateRange.endDate) {
            fetchWebhookFunnelAnalyticsData();
            fetchWebhookReportsAnalyticsData();
            fetchWebhookReportsData();
        }
    };

    // Handle global date change - refetch all APIs
    const handleDateChange = ({ startDate, endDate }) => {
        setDateRange({
            startDate: startDate,
            endDate: endDate
        });
    };

    // UseEffect for selectedTemplateId changes - add this after other useEffects
    useEffect(() => {
        if (dateRange.startDate && dateRange.endDate) {
            fetchAllData();
        }
    }, [selectedTemplateId]);
    // Refetch all APIs when global date range changes
    useEffect(() => {
        if (dateRange.startDate != null && dateRange.endDate != null) {
            setCompareDateRange({
                compareStartDate: dateRange.startDate,
                compareEndDate: dateRange.endDate
            })
            fetchAllData();
        }
    }, [dateRange]);

    // Refetch funnel analytics when compare date range changes
    useEffect(() => {
        const prevCompareDate = prevCompareDateRef.current;

        const compareDatesChanged =
            prevCompareDate.compareStartDate !== compareDateRange.compareStartDate ||
            prevCompareDate.compareEndDate !== compareDateRange.compareEndDate;

        if (compareDatesChanged) {
            fetchWebhookFunnelAnalyticsData();
            prevCompareDateRef.current = compareDateRange;
        }
    }, [compareDateRange]);

    // Refetch reports analytics when groupBy changes
    useEffect(() => {
        const prevGroupBy = prevGroupByRef.current;

        if (prevGroupBy !== groupBy) {
            fetchWebhookReportsAnalyticsData();
            prevGroupByRef.current = groupBy;
        }
    }, [groupBy]);

    // Refetch webhookReports when table filters change
    useEffect(() => {
        const prevFilters = prevTableFiltersRef.current;

        const filtersChanged =
            prevFilters.sortBy !== tableFilters.sortBy ||
            prevFilters.orderBy !== tableFilters.orderBy ||
            prevFilters.user_reply !== tableFilters.user_reply ||
            prevFilters.customer_number !== tableFilters.customer_number;

        if (filtersChanged) {
            fetchWebhookReportsData();
            prevTableFiltersRef.current = tableFilters;
        }
    }, [tableFilters]);

    // Refetch webhookReports when pagination changes
    useEffect(() => {
        fetchWebhookReportsData();
    }, [currentParams]);

    // Initial useEffect to fetch templates on mount
    useEffect(() => {
        fetchTemplatesData();
    }, []);

    // Extract summary data safely
    const summary = webhookReportsAnalyticsData?.data?.summary || {};
    const stats = {
        totalMessagesSent: summary.total_messages_sent || 0,
        responseRate: summary.response_rate || 0,
        avgResponseTime: summary.avg_response_time_formatted || '0h 0m',
        customerEngagementRate: summary.customer_engagement_rate || 0,
        uniqueCustomers: summary.unique_customers || 0,
        customersResponded: summary.customers_responded || 0,
        noResponseRate: summary.no_response_rate || 0
    };

    // Extract chart data safely
    const chartData = webhookReportsAnalyticsData?.data || null;

    return (
        <div className={`${!data && 'mt-10 mx-6'} sm:mt-10`}>
            {/* Header Section - Only Global Date Range */}
            <TemplateAnalyticsHeader
                dateRange={dateRange}
                onDateChange={handleDateChange}
                templatesData={templatesData}
                loadingTemplates={loadingTemplates}
                selectedTemplateId={selectedTemplateId}
                onTemplateFilterChange={handleTemplateFilterChange}
            />

            {/* Content Section */}
            <div style={{ padding: '20px' }}>
                {/* Conversion Funnel - Has its own filter button */}
                <ConversionFunnel
                    loading={loadingFunnelAnalytics}
                    data={webhookFunnelAnalyticsData}
                    compareDateRange={compareDateRange}
                    onCompareDateChange={setCompareDateRange}
                    onRefresh={fetchWebhookFunnelAnalyticsData}
                />

                {/* Stats Grid - Has its own filter button */}
                <div style={{ marginTop: '20px' }}>
                    <StatsGrid
                        loading={loadingReportsAnalytics}
                        stats={stats}
                        groupBy={groupBy}
                        onGroupByChange={setGroupBy}
                        onRefresh={fetchWebhookReportsAnalyticsData}
                    />
                </div>

                {/* Charts Grid - Shares groupBy filter with StatsGrid */}
                <Grid container spacing={3} style={{ marginTop: '20px' }}>
                    <Grid item xs={12} md={6}>
                        <ResponseBreakdownChart
                            loading={loadingReportsAnalytics}
                            data={chartData}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <StatusDistributionChart
                            loading={loadingReportsAnalytics}
                            data={chartData}
                        />
                    </Grid>
                </Grid>

                {/* Response Time Distribution Chart & Flow Analytics */}
                <Grid container spacing={3} style={{ marginTop: '20px' }}>
                    <Grid item xs={12} md={chartData?.flow_analytics ? 6 : 12}>
                        <ResponseTimeDistributionChart
                            loading={loadingReportsAnalytics}
                            data={chartData}
                        />
                    </Grid>
                    {chartData?.flow_analytics && (
                        <Grid item xs={12} md={6}>
                            <FlowAnalyticsChart
                                loading={loadingReportsAnalytics}
                                data={chartData?.flow_analytics}
                            />
                        </Grid>
                    )}
                </Grid>

                {/* Template Reports Table - Has its own filter and export buttons */}
                <div style={{ marginTop: '20px' }}>
                    <TemplateReportsTable
                        loading={loadingReports}
                        data={webhookReportsData}
                        currentParams={currentParams}
                        setCurrentParams={setCurrentParams}
                        totalItems={totalItems}
                        tableFilters={tableFilters}
                        onTableFiltersChange={setTableFilters}
                        dateRange={dateRange}
                        clientId={data?.id}
                    />
                </div>
            </div>
        </div>
    );
}

export default TemplateAnalytics;